import { useState } from "react"
import { Link } from "react-router-dom"
import { ChevronDown, ChevronRight, LevelIcon, PriorityIcon, StatusIcon, TypeIcon } from "./icons"
import {
  Avatar,
  Badge,
  Progress,
  describeSpentOfEstimate,
  formatDuration,
  formatSpentOfEstimate,
  isOverdue,
  relativeTime,
} from "./ui"
import type { Status, Task, TaskLevel } from "../lib/api"
import { useBoardConfig } from "../lib/boardConfig"
import { CHILDREN_LABEL } from "../lib/levels"
import { isLaneOpen, NO_FOLDS, toggleLane, type LaneFolds } from "../lib/laneFolds"
import { groupByEpic, nestChildren, type Lane } from "../lib/taskTree"

// The board and the list. Both render the same card vocabulary, so a task looks
// like itself whichever view you are in.
//
// DRAGGING uses the browser's own drag-and-drop rather than a library. It costs
// nothing to ship, and the two things a board needs from it — where the pointer
// is, and what was released — are exactly what the native events report. The
// price is touch: HTML5 drag does not fire on a touchscreen. That is why every
// card also carries a status menu, which is the same escape hatch keyboard
// users need.

/** Where a dragged card would land: which column, and at which index in it. */
type DropAt = { status: Status; index: number }

export type MoveHandler = (taskId: string, status: Status, index: number) => void

// ---------- the card ----------

export function TaskCard({
  task,
  onMove,
  draggable = true,
  onDragStart,
  onDragEnd,
  parentKey,
  hideParent = false,
}: {
  task: Task
  onMove?: MoveHandler
  draggable?: boolean
  onDragStart?: () => void
  onDragEnd?: () => void
  /** The parent's key, when the view knows it. A card carries only the id
   *  unless the list joined the key in. */
  parentKey?: string | null
  hideParent?: boolean
}) {
  const assignee = task.assigneeName
  const overdue = isOverdue(task.dueDate, task.status)

  return (
    <div
      draggable={draggable}
      onDragStart={(event) => {
        // Both are set: the id is what the drop handler reads, and the plain
        // text is what other applications see if the card is dropped outside.
        event.dataTransfer.setData("text/x-task-id", task.id)
        event.dataTransfer.setData("text/plain", task.key)
        event.dataTransfer.effectAllowed = "move"
        onDragStart?.()
      }}
      onDragEnd={onDragEnd}
      // Without it a sorted board just ignores the grab, and nothing says why.
      title={draggable ? undefined : "Cards are sorted. Choose Sort: Board order to drag them."}
      className={`group flex flex-col gap-2 rounded-lg border border-border bg-surface p-2.5 transition-colors hover:border-border-strong ${
        draggable ? "cursor-grab active:cursor-grabbing" : ""
      }`}
    >
      <div className="flex items-center gap-1.5">
        {!(hideParent && task.level === "task") && <LevelIcon level={task.level} />}
        {task.level !== "epic" && <TypeIcon type={task.type} />}
        <Link
          to={`/tasks/${task.id}`}
          className="font-mono text-xs text-muted hover:text-foreground hover:underline"
        >
          {task.key}
        </Link>
        <span className="ml-auto flex items-center gap-1">
          {task.priority !== "none" && <PriorityIcon priority={task.priority} />}
          {onMove && <StatusMenu task={task} onMove={onMove} />}
        </span>
      </div>

      <Link to={`/tasks/${task.id}`} title={task.title} className="line-clamp-3 text-sm leading-snug text-foreground hover:underline">
        {task.title}
      </Link>

      {(task.labels.length > 0 || task.sprintName || (task.parentTaskId && !hideParent)) && (
        <div className="flex flex-wrap gap-1">
          {task.parentTaskId && !hideParent && (
            <ParentChip id={task.parentTaskId} parentKey={parentKey ?? task.parentKey} level={task.level} />
          )}
          {task.sprintName && <span className="max-w-full truncate text-xs text-muted" title={`Sprint: ${task.sprintName}`}>{task.sprintName}</span>}
          {task.labels.slice(0, 3).map((label) => (
            <Badge key={label}>{label}</Badge>
          ))}
          {task.labels.length > 3 && <Badge title={task.labels.slice(3).join(", ")}>+{task.labels.length - 3}</Badge>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
        <Progress done={task.childDone ?? 0} total={task.childTotal ?? 0} label={CHILDREN_LABEL[task.level]} />
        <LoggedTime task={task} />
        {task.dueDate && (
          <span className={overdue ? "font-medium text-danger" : ""} title={`Due ${task.dueDate.slice(0, 10)}`}>
            {relativeTime(task.dueDate)}
          </span>
        )}
        <span className="ml-auto">
          {assignee ? <Avatar name={assignee} id={task.assigneeId} size="xs" /> : <span>Unassigned</span>}
        </span>
      </div>
    </div>
  )
}

/** "↑ ASC-12" on a task or sub-task: the epic or task it belongs to, one click
 *  away. */
function ParentChip({ id, parentKey, level }: { id: string; parentKey?: string | null; level: TaskLevel }) {
  return (
    <Link
      to={`/tasks/${id}`}
      title={level === "subtask" ? "Parent task" : "Epic"}
      className="inline-flex items-center rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted hover:border-border-strong hover:text-foreground"
    >
      ↑ {parentKey ?? "parent"}
    </Link>
  )
}

/** The non-drag way to move a card. Present on every card because HTML5 drag
 *  does not exist on a touchscreen and cannot be driven from a keyboard. */
function StatusMenu({ task, onMove }: { task: Task; onMove: MoveHandler }) {
  const { statuses: menuStatuses } = useBoardConfig()
  const [open, setOpen] = useState(false)

  return (
    <span className="relative">
      <button
        type="button"
        aria-label="Move to status"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex rounded p-0.5 text-muted hover:text-foreground"
      >
        <StatusIcon status={task.status} />
      </button>
      {open && (
        <span className="absolute right-0 z-30 mt-1 flex w-36 flex-col rounded-md border border-border bg-surface py-1 shadow-lg">
          {menuStatuses.map((status) => (
            <button
              key={status.id}
              type="button"
              onClick={() => onMove(task.id, status.id, 0)}
              className="flex items-center gap-2 px-2.5 py-1.5 text-left text-xs text-foreground hover:bg-surface-muted"
            >
              <StatusIcon status={status.id} />
              {status.name}
            </button>
          ))}
        </span>
      )}
    </span>
  )
}

// ---------- board ----------

export function BoardView({
  tasks,
  onMove,
  swimlanes = false,
  filterKey = "",
  sortable = true,
}: {
  tasks: Task[]
  onMove: MoveHandler
  swimlanes?: boolean
  filterKey?: string
  /** False while a field sort is active: a drop position means nothing in a
   *  sorted column, so cards are not draggable and no drop is accepted. The
   *  status menu on each card still moves a task between columns. */
  sortable?: boolean
}) {
  const { statuses } = useBoardConfig()
  const keys = keysById(tasks)
  const parentKeyOf = (task: Task) => task.parentKey ?? (task.parentTaskId ? keys.get(task.parentTaskId) : undefined)
  const lanes = swimlanes ? groupByEpic(tasks) : []
  // A board with many epics opens as its list of epics, not a wall of cards;
  // a filter opens every lane so its matches show. See lib/laneFolds.
  const [folds, setFolds] = useState<LaneFolds>(NO_FOLDS)
  const [dragging, setDragging] = useState<string | null>(null)
  const [dropAt, setDropAt] = useState<(DropAt & { lane: string }) | null>(null)
  const drag = {
    dragging,
    dropAt,
    setDropAt,
    start: (id: string) => setDragging(id),
    clear: () => {
      setDragging(null)
      setDropAt(null)
    },
  }
  const cards = lanes.length ? lanes.flatMap((lane) => lane.items) : tasks
  const grid = { gridTemplateColumns: `repeat(${statuses.length}, minmax(180px, 1fr))` }

  // Reordering remains column-wide; a lane-local drop indexes the same cards
  // before/after the drop target without changing their epic membership.
  const laneMove = (laneTasks: Task[]): MoveHandler => (taskId, status, index) => {
    const column = tasks.filter((task) => task.status === status && task.id !== taskId)
    const laneColumn = laneTasks.filter((task) => task.status === status && task.id !== taskId)
    const before = laneColumn[index]
    const after = laneColumn[index - 1]
    const at = before ? column.indexOf(before) : after ? column.indexOf(after) + 1 : column.length
    onMove(taskId, status, Math.max(0, at))
  }
  const toggle = (id: string) => setFolds((current) => toggleLane(current, id, filterKey, lanes.length))

  return (
    <div data-board-scroll className="relative h-full min-h-0 overflow-auto overscroll-contain">
      <div className="relative w-full" style={{ minWidth: statuses.length * 180 + Math.max(0, statuses.length - 1) * 12 }}>
        <div data-board-headers className="sticky top-0 z-20 grid gap-3 border-b border-border bg-surface-muted py-3" style={grid}>
          {statuses.map((status) => (
            <header key={status.id} className="flex min-w-0 items-center gap-2 px-2">
              <StatusIcon status={status.id} />
              <h2 className="text-xs font-medium text-foreground">{status.name}</h2>
              <span className="ml-auto font-mono text-xs text-muted tabular-nums">{cards.filter((task) => task.status === status.id).length}</span>
            </header>
          ))}
        </div>
        {lanes.length ? lanes.map((lane) => {
          const id = lane.epicId ?? "none"
          const folded = !isLaneOpen(folds, id, filterKey, lanes.length)
          return (
            <section key={id} aria-label={laneName(lane)} className="border-b border-border">
              <LaneHeader lane={lane} collapsed={folded} onToggle={() => toggle(id)} controls={`epic-lane-${id}`} />
              <div id={`epic-lane-${id}`} hidden={folded}>
                <Columns lane={id} tasks={lane.items} onMove={laneMove(lane.items)} parentKeyOf={parentKeyOf} drag={drag} epicId={lane.epicId} sortable={sortable} />
              </div>
            </section>
          )
        }) : <Columns lane="all" tasks={tasks} onMove={onMove} parentKeyOf={parentKeyOf} drag={drag} sortable={sortable} />}
      </div>
    </div>
  )
}

const laneName = (lane: Lane<Task>) =>
  lane.epicId ? `${lane.epicKey ?? "Epic"}${lane.epic ? ` ${lane.epic.title}` : ""}` : "No epic"

function LaneHeader({ lane, collapsed, onToggle, controls }: {
  lane: Lane<Task>
  collapsed?: boolean
  onToggle?: () => void
  controls?: string
}) {
  const epic = lane.epic
  return (
    <header className="flex min-w-0 items-center gap-2 px-1 py-3">
      {onToggle && (
        <button type="button" onClick={onToggle} aria-expanded={!collapsed} aria-controls={controls}
          aria-label={`${collapsed ? "Expand" : "Collapse"} ${laneName(lane)}`}
          className="shrink-0 rounded p-1 text-muted hover:bg-surface">
          {collapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
        </button>
      )}
      {lane.epicId ? (
        <>
          <LevelIcon level="epic" />
          <Link to={`/tasks/${lane.epicId}`} className="shrink-0 font-mono text-xs text-muted hover:text-foreground hover:underline">
            {lane.epicKey ?? "Epic"}
          </Link>
          {epic ? <Link to={`/tasks/${epic.id}`} title={epic.title} className="min-w-0 truncate text-sm font-medium text-foreground hover:underline">{epic.title}</Link>
            : <span className="text-xs text-muted">Not in this view</span>}
          {epic && <Progress done={epic.childDone ?? 0} total={epic.childTotal ?? 0} label="tasks" />}
        </>
      ) : <span className="text-sm font-medium text-muted">No epic</span>}
      <span className="ml-auto shrink-0 font-mono text-xs text-muted tabular-nums">{lane.items.length}</span>
    </header>
  )
}

type DragState = {
  dragging: string | null
  dropAt: (DropAt & { lane: string }) | null
  setDropAt: (next: (DropAt & { lane: string }) | null | ((current: (DropAt & { lane: string }) | null) => (DropAt & { lane: string }) | null)) => void
  start: (id: string) => void
  clear: () => void
}

function Columns({ lane, tasks, onMove, parentKeyOf, drag, epicId, sortable }: {
  lane: string
  tasks: Task[]
  onMove: MoveHandler
  parentKeyOf: (task: Task) => string | null | undefined
  drag: DragState
  epicId?: string | null
  sortable: boolean
}) {
  const { statuses } = useBoardConfig()
  const { dragging, dropAt, setDropAt } = drag
  const here = (status: Status) => dropAt?.lane === lane && dropAt.status === status
  return (
    <div className="relative grid gap-3 pb-4" style={{ gridTemplateColumns: `repeat(${statuses.length}, minmax(180px, 1fr))` }}>
      {statuses.map(({ id: status, name: statusName }) => {
        const column = tasks.filter((task) => task.status === status)
        return (
          <section key={status} aria-label={statusName}
            onDragOver={(event) => {
              if (!sortable || !dragging) return
              event.preventDefault()
              event.dataTransfer.dropEffect = "move"
              setDropAt((current) => current?.lane === lane && current.status === status ? current : { lane, status, index: column.length })
            }}
            onDrop={(event) => {
              if (!sortable) return
              event.preventDefault()
              const id = event.dataTransfer.getData("text/x-task-id") || dragging
              if (id) onMove(id, status, here(status) && dropAt ? dropAt.index : column.length)
              drag.clear()
            }}
            className={`relative flex min-h-14 min-w-0 flex-col gap-2 rounded-lg px-1 py-2 transition-colors ${here(status) ? "bg-accent-soft" : ""}`}>
            {column.map((task, index) => (
              <div key={task.id}
                onDragOver={(event) => {
                  if (!sortable || !dragging) return
                  event.preventDefault()
                  event.stopPropagation()
                  const box = event.currentTarget.getBoundingClientRect()
                  setDropAt({ lane, status, index: event.clientY > box.top + box.height / 2 ? index + 1 : index })
                }}
                className={dragging === task.id ? "opacity-40" : ""}>
                {here(status) && dropAt?.index === index && <DropLine />}
                <TaskCard task={task} onMove={onMove} parentKey={parentKeyOf(task)} draggable={sortable}
                  hideParent={Boolean(epicId && task.parentTaskId === epicId)}
                  onDragStart={() => drag.start(task.id)} onDragEnd={drag.clear} />
              </div>
            ))}
            {here(status) && dropAt && dropAt.index >= column.length && <DropLine />}
            {column.length === 0 && <p className="py-3 text-center text-xs text-muted" aria-label="No tasks in this status">—</p>}
          </section>
        )
      })}
    </div>
  )
}

function keysById(tasks: Task[]) {
  return new Map(tasks.map((task) => [task.id, task.key]))
}

const DropLine = () => <div className="mb-2 h-0.5 rounded-full bg-interactive" aria-hidden="true" />

// ---------- list ----------

export function ListView({
  tasks,
  onMove,
  showProject,
  swimlanes = false,
}: {
  tasks: Task[]
  onMove?: MoveHandler
  /** Names the project on each row. On by default for lists that span more
   *  than one, where a key alone does not say where a task lives. */
  showProject?: boolean
  /** One group per epic, then "No epic", each with its status sections. */
  swimlanes?: boolean
}) {
  const keys = keysById(tasks)
  const parentKeyOf = (task: Task) => task.parentKey ?? (task.parentTaskId ? keys.get(task.parentTaskId) : undefined)
  const lanes = swimlanes ? groupByEpic(tasks) : []
  if (!lanes.length) {
    return <StatusSections tasks={tasks} onMove={onMove} showProject={showProject} parentKeyOf={parentKeyOf} />
  }
  return (
    <div className="flex flex-col gap-7">
      {lanes.map((lane) => (
        <section key={lane.epicId ?? "none"} aria-label={laneName(lane)} className="flex flex-col gap-2">
          <LaneHeader lane={lane} />
          {lane.items.length ? (
            <div className="pl-2">
              <StatusSections
                tasks={lane.items}
                onMove={onMove}
                showProject={showProject}
                parentKeyOf={parentKeyOf}
              />
            </div>
          ) : (
            <p className="px-1 text-xs text-faint">No tasks in this epic yet.</p>
          )}
        </section>
      ))}
    </div>
  )
}

function StatusSections({
  tasks,
  onMove,
  showProject,
  parentKeyOf,
}: {
  tasks: Task[]
  onMove?: MoveHandler
  showProject?: boolean
  parentKeyOf: (task: Task) => string | null | undefined
}) {
  const { statuses: listStatuses } = useBoardConfig()
  return (
    <div className="flex flex-col gap-5">
      {listStatuses.map(({ id: status, name: statusName }) => {
        const rows = tasks.filter((task) => task.status === status)
        if (!rows.length) return null

        return (
          <section key={status} className="flex flex-col">
            <header className="flex items-center gap-2 border-b border-border px-1 pb-1.5">
              <StatusIcon status={status} />
              <h2 className="text-xs font-medium text-foreground">{statusName}</h2>
              <span className="font-mono text-[11px] text-faint tabular-nums">{rows.length}</span>
            </header>
            <ul>
              {nestChildren(rows).map(({ task, depth }) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onMove={onMove}
                  showProject={showProject}
                  depth={depth}
                  parentKey={parentKeyOf(task)}
                />
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function TaskRow({
  task,
  onMove,
  showProject,
  depth = 0,
  parentKey,
}: {
  task: Task
  onMove?: MoveHandler
  showProject?: boolean
  /** How far under its parent it sits in this list: 0, 1 or 2. Nested rows
   *  are indented and carry no parent chip. */
  depth?: number
  parentKey?: string | null
}) {
  const nested = depth > 0
  const assignee = task.assigneeName
  const overdue = isOverdue(task.dueDate, task.status)

  return (
    <li
      className={`group flex items-center gap-3 border-b border-border py-2 pr-1 last:border-0 hover:bg-surface-muted/60 ${
        depth >= 2 ? "pl-13" : depth === 1 ? "pl-7" : "pl-1"
      }`}
    >
      <LevelIcon level={task.level} />
      {task.level !== "epic" && <TypeIcon type={task.type} />}
      <PriorityIcon priority={task.priority} />
      <Link to={`/tasks/${task.id}`} className="font-mono text-[11px] text-faint hover:text-muted">
        {task.key}
      </Link>
      <Link to={`/tasks/${task.id}`} className="min-w-0 flex-1 truncate text-sm text-foreground hover:underline">
        {task.title}
      </Link>

      {showProject && task.projectName && (
        <Link
          to={`/projects/${task.projectId}`}
          className="hidden shrink-0 text-xs text-muted hover:underline sm:inline"
        >
          {task.projectName}
        </Link>
      )}

      <div className="hidden shrink-0 items-center gap-1 md:flex">
        {!nested && task.parentTaskId && <ParentChip id={task.parentTaskId} parentKey={parentKey} level={task.level} />}
        {task.sprintName && <Badge tone="accent" title="Sprint">{task.sprintName}</Badge>}
        {task.labels.slice(0, 2).map((label) => (
          <Badge key={label}>{label}</Badge>
        ))}
      </div>

      <span className="hidden shrink-0 items-center gap-2 md:inline-flex">
        <Progress done={task.childDone ?? 0} total={task.childTotal ?? 0} label={CHILDREN_LABEL[task.level]} />
      </span>

      <LoggedTime task={task} className="hidden shrink-0 font-mono text-[10px] tabular-nums lg:inline" tone="text-faint" />

      {task.dueDate && (
        <span className={`hidden shrink-0 text-[10px] lg:inline ${overdue ? "text-danger" : "text-faint"}`}>
          {relativeTime(task.dueDate)}
        </span>
      )}

      {onMove && <StatusMenu task={task} onMove={onMove} />}
      <Avatar name={assignee} id={task.assigneeId} size="xs" />
    </li>
  )
}

/** Time logged, and against the estimate when there is one: "2h / 3h". An
 *  estimated task shows even before anything is logged, because "0h / 3h" is
 *  the point of estimating. Red once the work has run past the estimate. */
function LoggedTime({ task, className = "", tone = "" }: { task: Task; className?: string; tone?: string }) {
  const logged = task.minutesLogged ?? 0
  const estimate = task.estimateMinutes
  if (!estimate) {
    if (!logged) return null
    return (
      <span className={`${className} ${tone}`} title="Time logged by people">
        {formatDuration(logged)}
      </span>
    )
  }
  return (
    <span
      className={`${className} ${logged > estimate ? "font-medium text-danger" : tone}`}
      title={describeSpentOfEstimate(logged, estimate)}
    >
      {formatSpentOfEstimate(logged, estimate)}
    </span>
  )
}
